import JSZip from "jszip";
import { db, kv } from "./db";
import { verifyVersion } from "./store";
import { sha256Blob } from "./util";

export async function exportBackup() {
  const zip = new JSZip();
  const files = await db.all("files");
  const meta = files.map(({ blob, ...rest }) => rest);
  files.forEach((f) => zip.file(`files/${f.id}`, f.blob));
  const data = {
    app: "StarVault", format: 1, exportedAt: new Date().toISOString(),
    folders: await db.all("folders"), notes: await db.all("notes"),
    versions: await db.all("versions"), files: meta, profile: (await kv.get("profile")) || null,
  };
  const manifest = {
    fileCount: files.length, totalBytes: files.reduce((s, f) => s + (f.size || 0), 0),
    notes: data.notes.length, versions: data.versions.length,
  };
  zip.file("backup.json", JSON.stringify(data));
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  return { blob: await zip.generateAsync({ type: "blob" }), manifest };
}

const BAD = new Set(["__proto__", "constructor", "prototype"]);
const clean = (v) => (Array.isArray(v) ? v.map(clean)
  : v && typeof v === "object" ? Object.fromEntries(Object.entries(v).filter(([k]) => !BAD.has(k)).map(([k, x]) => [k, clean(x)])) : v);
const ID = /^[\w-]{1,80}$/;
const ok = (r, keys) => r && typeof r === "object" && !Array.isArray(r) && typeof r.id === "string" && ID.test(r.id) && keys.every((k) => typeof r[k] === "string");
const list = (x, keys) => (Array.isArray(x) ? x : []).map(clean).filter((r) => ok(r, keys));
const FUTURE = () => new Date(Date.now() + 86400000).toISOString();

export async function readBackup(file) {
  const zip = await JSZip.loadAsync(file);
  const raw = JSON.parse(await zip.file("backup.json").async("string"));
  const manifest = clean(JSON.parse(await zip.file("manifest.json").async("string")));
  if (raw?.app !== "StarVault") throw new Error("Not a StarVault backup");
  const notes = list(raw.notes, ["folderId", "createdAt", "updatedAt"]);
  const data = {
    folders: list(raw.folders, ["name"]),
    notes,
    versions: list(raw.versions, ["noteId", "savedAt", "hash"]).filter((v) => Number.isInteger(v.n)),
    files: list(raw.files, ["noteId", "name", "mime"]).filter((f) => Number.isFinite(f.size)),
    profile: raw.profile && typeof raw.profile === "object" && !Array.isArray(raw.profile) ? clean(raw.profile) : null,
    dropped: ["folders", "notes", "versions", "files"].reduce((s, k) => s + (Array.isArray(raw[k]) ? raw[k].length : 0), 0),
  };
  data.dropped -= data.folders.length + notes.length + data.versions.length + data.files.length;
  const overwrites = [];
  for (const n of notes) {
    const local = await db.get("notes", n.id);
    if (local && n.updatedAt > local.updatedAt && n.updatedAt < FUTURE()) overwrites.push(n.title || "Untitled");
  }
  return { zip, data, manifest, overwrites };
}

export async function importBackup({ zip, data, manifest }, { overwrite = false } = {}) {
  const r = { foldersAdded: 0, notesAdded: 0, notesUpdated: 0, notesKept: 0, versionsAdded: 0, filesAdded: 0, filesFound: 0, bytesFound: 0, sizeMismatches: 0, rejected: data.dropped };
  for (const f of data.folders) if (!(await db.get("folders", f.id))) { await db.put("folders", f); r.foldersAdded++; }
  for (const n of data.notes) {
    const local = await db.get("notes", n.id);
    if (!local) { await db.put("notes", n); r.notesAdded++; }
    else if (n.updatedAt > local.updatedAt && n.updatedAt < FUTURE()) {
      if (!overwrite) { r.notesKept++; continue; }
      await db.put("notes", { ...n, versionCount: Math.max(n.versionCount || 0, local.versionCount || 0) }); r.notesUpdated++;
    }
  }
  for (const v of data.versions) {
    if (await db.get("versions", v.id)) continue;
    if (!(await verifyVersion(v)) || !(await db.get("notes", v.noteId))) { r.rejected++; continue; }
    await db.addVersion({ ...v, importedAt: new Date().toISOString() }); r.versionsAdded++;
  }
  for (const m of data.files) {
    const entry = zip.file(`files/${m.id}`);
    if (!entry) continue;
    const blob = new Blob([await entry.async("arraybuffer")], { type: m.mime });
    r.filesFound++; r.bytesFound += blob.size;
    if (blob.size !== m.size) r.sizeMismatches++;
    if (await db.get("files", m.id)) continue;
    if ((await sha256Blob(blob)) !== m.hash) { r.rejected++; continue; }
    await db.put("files", { ...m, blob }); r.filesAdded++;
  }
  for (const n of await db.all("notes")) {
    const vs = (await db.byIndex("versions", "noteId", n.id)).sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    if (vs.length && vs.length !== n.versionCount) {
      await db.put("notes", { ...n, versionCount: Math.max(...vs.map((v) => v.n)), lastHash: vs[vs.length - 1].hash });
    }
  }
  if (data.profile && !(await kv.get("profile"))) await kv.set("profile", data.profile);
  r.verified = r.filesFound === manifest.fileCount && r.bytesFound === manifest.totalBytes && r.sizeMismatches === 0 && r.rejected === 0;
  return { manifest, result: r };
}
